import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Divider, ListItem, ListItemText, Tooltip, Typography, Box, FormHelperText } from '@mui/material'
import T from 'T'
import { toast } from 'react-toastify'
import { shallowEqual, useSelector } from 'react-redux'
import get from 'lodash/get'
import { useLazyGetPaymentsDataQuery, useUpdateAutopayMutation } from 'api/payments/payments'
import { getAmountDue } from 'utils/payment'
import { formatDateToFEFormatDateFns } from 'utils/date'
import HHFormMuiSwitch from 'components/form-fields/v5/HHFormMuiSwitch'
import { useForm } from 'react-hook-form'
import { useLazyGetAllPaymentMethodsQuery } from 'api/payment-gateway/paymentGateway'
import { DEFAULT_AUTOPAY_TIMING } from 'settings/constants/payment'
import BillingAddress from 'components/payments/BillingAddress'
import HHFormSelectPaymentMethodField from 'components/payments/HHFormSelectPaymentMethodField'
import PaymentMethodListItem from 'components/common/PaymentMethodListItem'
import TermsNConditionsDialog from 'components/payments/TermsNConditionsDialog'
import LoadingButton from '@mui/lab/LoadingButton'
import { handleError } from 'utils/error'
import usePaymentProvider from 'hooks/usePaymentProvider'
import HHDisplayMoney from '../HHDisplayMoney'
import PaymentSummarySkeleton from './PaymentSummarySkeleton'

const PaymentSummary = () => {
  const businessId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessId'), shallowEqual)
  const accountId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.accountId'), shallowEqual)

  const [getPaymentsData, { data: paymentsData, isFetching: isFetchingPayments }] = useLazyGetPaymentsDataQuery()
  const [getAllPaymentMethods, { data: paymentMethodsData, isFetching: isFetchingPaymentMethods }] = useLazyGetAllPaymentMethodsQuery()

  const { isInactive } = usePaymentProvider()

  const [isOpenTermsNConditionsDialog, setIsOpenTermsNConditionsDialog] = useState(false)

  const openTermsNConditionsDialog = () => {
    setIsOpenTermsNConditionsDialog(true)
  }

  const closeTermsNConditionsDialog = () => {
    setIsOpenTermsNConditionsDialog(false)
  }

  const unpaidInvoices = useMemo(() => get(paymentsData, 'unpaidInvoices', []), [paymentsData])
  const currentAmountDue = useMemo(() => getAmountDue(unpaidInvoices), [unpaidInvoices])
  const nextAmountDueDate = useMemo(
    () => (currentAmountDue > 0 ? formatDateToFEFormatDateFns(new Date(get(paymentsData, 'dueDate'))) : 'NA'),
    [currentAmountDue, paymentsData]
  )

  const paymentMethods = useMemo(() => get(paymentMethodsData, 'paymentMethods', []), [paymentMethodsData])
  const hasPaymentMethods = useMemo(() => paymentMethods.length > 0, [paymentMethods])

  const {
    control,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      autopay: false,
      paymentMethod: '',
    },
  })

  const watchAutopay = watch('autopay')
  const watchPaymentMethod = watch('paymentMethod')
  const isAutopayEnabled = useMemo(() => watchAutopay === true, [watchAutopay])

  const [updateAutopay, { isLoading: isUpdatingAutopay }] = useUpdateAutopayMutation()

  useEffect(() => {
    if (businessId && accountId) {
      getPaymentsData({ businessId, accountId })
      getAllPaymentMethods({ businessId, accountId })
    }
  }, [businessId, accountId])

  useEffect(() => {
    if (paymentsData) {
      const autopayVal = get(paymentsData, 'autopayInfo.autopay', false)
      const methodId = get(paymentsData, 'autopayInfo.paymentMethodId', '')
      reset({ autopay: autopayVal, paymentMethod: methodId })
    }
  }, [paymentsData])

  const disableSaveButton = useMemo(() => isAutopayEnabled && !watchPaymentMethod, [isAutopayEnabled, watchPaymentMethod])

  const handleSaveAutopay = async () => {
    const payload = {
      accountId,
      autopay: watchAutopay,
      paymentMethodId: watchAutopay ? watchPaymentMethod : null,
      autopayTiming: DEFAULT_AUTOPAY_TIMING,
    }
    try {
      await updateAutopay({ businessId, accountId, payload }).unwrap()
      toast.success(T.AUTOPAY_UPDATE_SUCCESS)
      // Refresh data and lock form to saved values (marks as not dirty)
      getPaymentsData({ businessId, accountId })
      reset({ autopay: watchAutopay, paymentMethod: watchPaymentMethod || '' })
    } catch (e) {
      handleError(e)
    }
  }

  const handleCancelAutopay = () => {
    const autopayVal = get(paymentsData, 'autopayInfo.autopay', false)
    const methodId = get(paymentsData, 'autopayInfo.paymentMethodId', '')
    reset({ autopay: autopayVal, paymentMethod: methodId })
  }

  const isLoading = (isFetchingPayments && !paymentsData) || (isFetchingPaymentMethods && !paymentMethodsData)
  if (isLoading) {
    return <PaymentSummarySkeleton />
  }

  return (
    <Box flex={1} height="100%">
      <Card elevation={0} variant="outlined" sx={{ width: '100%' }}>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{
              variant: 'h5',
              fontWeight: 400,
            }}
            secondaryTypographyProps={{
              variant: 'h2',
              fontWeight: 500,
              color: 'text.primary',
            }}
            primary={T.CURRENT_AMOUNT_DUE}
            secondary={<HHDisplayMoney value={currentAmountDue} formatToDollars={false} />}
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary={T.NEXT_AMOUNT_DUE}
            primaryTypographyProps={{
              color: 'text.secondary',
              variant: 'body1',
            }}
            secondary={nextAmountDueDate}
            secondaryTypographyProps={{
              color: 'text.primary',
            }}
          />
        </ListItem>
        <ListItem>
          <Tooltip title={isInactive ? T.PAYMENT_SERVICES_UNAVAILABLE : !hasPaymentMethods && T.ADD_PAYMENT_METHOD_TO_ENABLE_AUTOPAY} arrow>
            <span>
              <HHFormMuiSwitch disabled={isInactive || !hasPaymentMethods} name="autopay" control={control} label={T.AUTOPAY} />
            </span>
          </Tooltip>
        </ListItem>
        {isDirty && (
          <ListItem>
            <FormHelperText error>{T.AUTOPAY_SAVE_HELPER}</FormHelperText>
          </ListItem>
        )}
        <Divider />
        <BillingAddress />
        {isAutopayEnabled && !isInactive && (
          <>
            <ListItem>
              <ListItemText
                primary={T.STORED_PAYMENT_METHOD}
                primaryTypographyProps={{ color: 'text.secondary', variant: 'body1' }}
                secondary={
                  <>
                    {hasPaymentMethods && (
                      <>
                        <HHFormSelectPaymentMethodField
                          getOptionLabel={option => <PaymentMethodListItem method={option} />}
                          getOptionValue={option => option.id}
                          fullWidth
                          name="paymentMethod"
                          control={control}
                          options={paymentMethods}
                        />
                        {isAutopayEnabled && !watchPaymentMethod && <FormHelperText error>{T.PLEASE_SELECT_PAYMENT_METHOD}</FormHelperText>}
                      </>
                    )}
                    {!hasPaymentMethods && <Button>{T.ADD_PAYMENT_METHOD}</Button>}
                  </>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <>
                    <Typography sx={{ display: 'inline' }}>{T.AUTOPAY_ENROLMENT_MSG}</Typography>
                    <Button variant="text" sx={{ display: 'inline', p: 0, minWidth: 0 }} onClick={openTermsNConditionsDialog}>
                      {T.TERMS_N_CONDITIONS}
                    </Button>
                    .
                  </>
                }
              />
            </ListItem>
          </>
        )}
        {isDirty && (
          <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleCancelAutopay}>{T.CANCEL}</Button>
            <Tooltip
              disableHoverListener={!disableSaveButton}
              title={disableSaveButton ? T.PLEASE_SELECT_PAYMENT_METHOD_TO_PROCEED : ''}
              arrow
            >
              <span>
                <LoadingButton
                  loading={isUpdatingAutopay}
                  variant="contained"
                  disabled={disableSaveButton || isUpdatingAutopay}
                  onClick={handleSaveAutopay}
                >
                  {T.SAVE}
                </LoadingButton>
              </span>
            </Tooltip>
          </ListItem>
        )}
      </Card>
      <TermsNConditionsDialog isOpen={isOpenTermsNConditionsDialog} onClose={closeTermsNConditionsDialog} />
    </Box>
  )
}

PaymentSummary.propTypes = {}

export default PaymentSummary
